import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Loader2 } from 'lucide-react'
import { useAuthStore } from '@hooks/useAuthStore'
import { useClinics } from '@hooks/useClinics'
import { useToast } from '@hooks/use-toast'
import { Badge } from '@components/ui/badge'

export function ClinicSelectorPage() {
  const navigate = useNavigate()
  const { clinicsList, switchClinic } = useAuthStore()
  const { useSwitchClinic } = useClinics()
  const switchClinicMutation = useSwitchClinic()
  const { toast } = useToast()
  const [switchingClinicId, setSwitchingClinicId] = useState<string | null>(null)

  const handleSelect = async (clinicId: string) => {
    setSwitchingClinicId(clinicId)
    try {
      const tokens = await switchClinicMutation.mutateAsync(clinicId)
      switchClinic(clinicId, tokens)
      navigate('/agenda')
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to select clinic. Please try again.',
        variant: 'destructive',
      })
      setSwitchingClinicId(null)
    }
  }

  return (
    <div className="min-h-screen bg-ml-surface flex items-center justify-center p-8">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-headline text-ml-on-surface mb-2">
            Select a Clinic
          </h1>
          <p className="text-sm text-ml-on-surface-variant">
            Choose which clinic you want to work in
          </p>
        </div>

        <div className="space-y-3">
          {clinicsList.map((clinic) => (
            <button
              key={clinic.id}
              onClick={() => handleSelect(clinic.id)}
              disabled={switchClinicMutation.isPending}
              className="w-full flex items-center gap-4 p-5 rounded-2xl border border-ml-outline-variant/20 bg-ml-surface-container hover:bg-ml-surface-container-high hover:border-ml-primary/30 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-xl bg-ml-primary/10 flex items-center justify-center shrink-0">
                {switchingClinicId === clinic.id ? (
                  <Loader2 className="h-5 w-5 text-ml-primary animate-spin" />
                ) : (
                  <Building2 className="h-5 w-5 text-ml-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ml-on-surface truncate">
                    {clinic.name}
                  </span>
                  {clinic.is_owner && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Owner
                    </Badge>
                  )}
                </div>
                {clinic.role_name && (
                  <p className="text-xs text-ml-on-surface-variant mt-0.5 capitalize">
                    {clinic.role_name}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
